import { NextRequest, NextResponse } from 'next/server';

import { COOKIE_NAMES } from '../constants/cookies';
import {
  nonCurriculumRoutes,
  publicPages,
  mainRoutes,
  routes,
} from '../constants/routes';
import { PortalLink, Portal } from '../enums/portal.enum';

import { getPortalFromCountry } from './portalMapping';

export const curriculumSpecificPaths = mainRoutes
  .filter(
    (route) => !nonCurriculumRoutes.some((ncr) => ncr.link === route.link),
  )
  .map((route) => route.link);

const nonPortalPaths = [...publicPages, routes.PRICING];

export function shouldExcludeFromRedirect(path: string): boolean {
  const hasPortalPrefix = Object.values(PortalLink).some(
    (portalPath) => path.startsWith(portalPath + '/') || path === portalPath,
  );
  if (hasPortalPrefix) return true;

  if (nonPortalPaths.some((nonPortalPath) => path === nonPortalPath))
    return true;

  const isSystemPath =
    path.startsWith('/api') ||
    path.startsWith('/_next') ||
    path.startsWith('/static') ||
    path.includes('.');

  return isSystemPath;
}

export function handlePortalRedirection(
  request: NextRequest,
): NextResponse | null {
  const url = request.nextUrl.clone();
  const path = url.pathname;

  if (
    path.includes('[portal]') ||
    path.includes('undefined') ||
    shouldExcludeFromRedirect(path)
  ) {
    return null;
  }

  if (isCurriculumSpecificPath(path)) {
    return redirectToPortal(Portal.PHILIPPINES, path, request.url);
  }

  return redirectBasedOnUserPreference(request, path);
}

function isCurriculumSpecificPath(path: string): boolean {
  return curriculumSpecificPaths.some((currPath) => path === currPath);
}

function redirectToPortal(
  portal: Portal,
  path: string,
  baseUrl: string,
): NextResponse {
  const newUrl = `${PortalLink[portal]}${path}`;
  return NextResponse.redirect(new URL(newUrl, baseUrl));
}

function redirectBasedOnUserPreference(
  request: NextRequest,
  path: string,
): NextResponse | null {
  const cookies = request.cookies;

  const selectedPortal = cookies.get(COOKIE_NAMES.SELECTED_PORTAL)
    ?.value as Portal;
  if (selectedPortal && Object.values(Portal).includes(selectedPortal)) {
    return redirectToPortal(selectedPortal, path, request.url);
  }

  const country = cookies.get(COOKIE_NAMES.COUNTRY)?.value;
  if (country) {
    const portalFromCountry = getPortalFromCountry(country);
    if (portalFromCountry) {
      return redirectToPortal(portalFromCountry, path, request.url);
    }
  }

  return null;
}

export function isValidPortal(portalPath: string): boolean {
  return Object.values(PortalLink).some(
    (path) => path === `/${portalPath.toLowerCase()}`,
  );
}
